import { useCallback, useEffect, useState } from "react";
import toast from "react-hot-toast";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import {
  campersApi,
  type FieldPick,
  type MergePreview,
  type MergeResult,
  type MergeSelections,
  type PreviewCamper,
  type PreviewCompletion,
  type PreviewEnrollment,
  type RowPick,
} from "../../api/campers";
import { useDebounce } from "../../hooks/useDebounce";
import { useAuth } from "../../hooks/useAuth";
import type { Camper } from "../../types";

interface Props {
  loser: Camper;
  /** When provided, skips the winner-search step and starts at review. */
  winner?: Camper;
  onCancel: () => void;
  onSuccess: (result: MergeResult) => void;
}

type Step = "pick-winner" | "review" | "confirm";

type FieldSide = "winner" | "loser";

interface FieldChoices {
  firstName: FieldSide;
  lastName: FieldSide;
  dateOfBirth: FieldSide;
  team: FieldSide;
  campMinderPersonId: FieldSide;
  excludedFromReports: FieldSide;
  notes: FieldPick;
}

interface IncludeChoices {
  honors: boolean;
  assessments: boolean;
  linkedUsers: boolean;
}

const FIELD_LABELS: Record<keyof FieldChoices, string> = {
  firstName: "First name",
  lastName: "Last name",
  dateOfBirth: "Date of birth",
  team: "Team",
  campMinderPersonId: "CampMinder person ID",
  excludedFromReports: "Excluded from reports",
  notes: "Notes",
};

function formatFieldValue(field: keyof FieldChoices, c: PreviewCamper): string {
  const v = c[field];
  if (v == null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

function defaultFieldPick<T>(winner: T, loser: T): FieldSide {
  // Prefer the side that actually has a value when the other is empty.
  const empty = (v: T) => v == null || v === "" || v === false;
  if (empty(winner) && !empty(loser)) return "loser";
  return "winner";
}

function formatDateRange(start: string | null, end: string | null): string {
  if (!start && !end) return "";
  if (start && end) {
    if (start === end) return start;
    return `${start} → ${end}`;
  }
  return start ?? end ?? "";
}

function describeEnrollment(e: PreviewEnrollment | null): string {
  if (!e) return "—";
  const parts: string[] = [];
  if (e.cabinName) parts.push(`cabin ${e.cabinName}`);
  else if (e.cabinId != null) parts.push(`cabin #${e.cabinId}`);
  if (e.divisionName) parts.push(`division ${e.divisionName}`);
  return parts.length > 0 ? parts.join(" · ") : "no cabin";
}

function buildDefaultFields(preview: MergePreview): FieldChoices {
  const w = preview.winner;
  const l = preview.loser;
  return {
    firstName: defaultFieldPick(w.firstName, l.firstName),
    lastName: defaultFieldPick(w.lastName, l.lastName),
    dateOfBirth: defaultFieldPick(w.dateOfBirth, l.dateOfBirth),
    team: defaultFieldPick(w.team, l.team),
    campMinderPersonId: defaultFieldPick(w.campMinderPersonId, l.campMinderPersonId),
    excludedFromReports: w.excludedFromReports || !l.excludedFromReports ? "winner" : "loser",
    notes: l.notes && w.notes ? "concat" : l.notes && !w.notes ? "loser" : "winner",
  };
}

export default function CamperMergeForm({
  loser: initialLoser,
  winner: initialWinner,
  onCancel,
  onSuccess,
}: Props) {
  const { user } = useAuth();
  const showSyncBadge = user?.role !== "counselor" && user?.role !== "readonly";

  // The wizard owns its own loser/winner state so the "swap" button on the
  // review step can flip them and re-fetch the preview without forcing the
  // caller to re-mount the form.
  const [loser, setLoser] = useState<Camper>(initialLoser);
  const [winner, setWinner] = useState<Camper | null>(initialWinner ?? null);

  const [step, setStep] = useState<Step>(initialWinner ? "review" : "pick-winner");

  // Step 1 — winner search
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const [results, setResults] = useState<Camper[]>([]);
  const [searching, setSearching] = useState(false);

  // Loading & merge state
  const [previewLoading, setPreviewLoading] = useState(false);
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [merging, setMerging] = useState(false);

  // Selection state for the review step
  const [fields, setFields] = useState<FieldChoices | null>(null);
  // Default for non-conflicting loser ACs is "winner" (i.e., move them — auto-include).
  // For conflicting ACs, default is "winner" (keep winner's row) — admin can flip.
  const [completionPicks, setCompletionPicks] = useState<Record<number, RowPick>>({});
  const [enrollmentPicks, setEnrollmentPicks] = useState<Record<string, RowPick>>({});
  const [include, setInclude] = useState<IncludeChoices>({
    honors: true,
    assessments: true,
    linkedUsers: true,
  });

  useEffect(() => {
    if (winner) return;
    if (!debouncedSearch.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    campersApi
      .list({ search: debouncedSearch, pageSize: 10, includeArchived: "true" })
      .then((res) => setResults(res.data.filter((c) => c.id !== loser.id)))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, [debouncedSearch, loser.id, winner]);

  const loadPreview = useCallback(async (winnerCamper: Camper, loserCamper: Camper) => {
    setPreviewLoading(true);
    try {
      const p = await campersApi.mergePreview(winnerCamper.id, loserCamper.id);
      setPreview(p);
      setFields(buildDefaultFields(p));
      const cPicks: Record<number, RowPick> = {};
      for (const id of p.completionConflicts) cPicks[id] = "winner";
      setCompletionPicks(cPicks);
      const ePicks: Record<string, RowPick> = {};
      for (const k of p.enrollmentConflicts) ePicks[k] = "winner";
      setEnrollmentPicks(ePicks);
      setStep("review");
      return true;
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || "Failed to load merge preview");
      return false;
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  // When opened with a preselected winner, fetch the preview eagerly.
  useEffect(() => {
    if (initialWinner && !preview && !previewLoading) {
      void loadPreview(initialWinner, initialLoser);
    }
    // We only want this to run on mount; the swap button handles subsequent loads.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function continueToReview() {
    if (!winner) return;
    await loadPreview(winner, loser);
  }

  async function swapWinnerAndLoser() {
    if (!winner) return;
    const newWinner = loser;
    const newLoser = winner;
    setWinner(newWinner);
    setLoser(newLoser);
    await loadPreview(newWinner, newLoser);
  }

  async function submit() {
    if (!winner || !preview || !fields) return;
    setMerging(true);
    try {
      // Only send fields that differ from "winner" — keeps the request tidy.
      const fieldDiff: MergeSelections["fields"] = {};
      (Object.keys(fields) as (keyof FieldChoices)[]).forEach((k) => {
        const v = fields[k];
        if (k === "notes") {
          if (v !== "winner") fieldDiff.notes = v as FieldPick;
        } else if (v !== "winner" && v !== "concat") {
          (fieldDiff as Record<string, FieldSide>)[k] = v;
        }
      });

      // When both campers carry different CampMinder IDs the backend refuses
      // the merge unless the request names which ID survives — an implicit
      // "winner" default doesn't count as a choice. Send the pick explicitly.
      if (preview.campMinderConflict) {
        fieldDiff.campMinderPersonId = fields.campMinderPersonId;
      }

      // Send picks for every loser AC — both for explicit conflict resolution
      // and to make "drop" choices for loser-only ACs work.
      const completions: Record<number, RowPick> = { ...completionPicks };
      for (const lc of preview.loser.completions) {
        if (!(lc.achievementCreditId in completions)) {
          completions[lc.achievementCreditId] = "winner"; // auto-include via default
        }
      }

      const enrollments: Record<string, RowPick> = { ...enrollmentPicks };
      for (const le of preview.loser.enrollments) {
        if (!(le.key in enrollments)) {
          enrollments[le.key] = "winner";
        }
      }

      const selections: MergeSelections = {
        fields: fieldDiff,
        completions,
        enrollments,
        include,
      };

      const result: MergeResult = await campersApi.merge(winner.id, loser.id, { selections });
      const m = result.moved;
      toast.success(
        `Merged. Moved ${m.completions} ACs, ${m.honors} honors, ${m.assessments} assessments, ${m.enrollments} enrollments.`
      );
      onSuccess(result);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      toast.error(msg || (err instanceof Error ? err.message : "Failed to merge"));
    } finally {
      setMerging(false);
    }
  }

  // ─── Step: Pick winner ───────────────────────────────────────────────────
  if (step === "pick-winner") {
    return (
      <div className="space-y-4">
        <div className="rounded-lg bg-stone-50 border border-stone-200 px-3 py-2 text-sm text-stone-700">
          Merging <strong>{loser.firstName} {loser.lastName}</strong> into another camper.
          You'll be able to choose which fields, ACs, and enrollments to keep before
          confirming.
        </div>

        {winner ? (
          <div className="border border-forest-300 bg-forest-50 rounded-lg px-3 py-2 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-forest-900">
                Winner: {winner.firstName} {winner.lastName}
              </p>
              <p className="text-xs text-forest-700">
                {winner.dateOfBirth ?? "no DOB"}
                {showSyncBadge && winner.campMinderPersonId != null && " · CampMinder"}
                {" · "}
                {winner.completionCount ?? 0} ACs
              </p>
            </div>
            <Button variant="ghost" size="sm" onClick={() => setWinner(null)}>
              Change
            </Button>
          </div>
        ) : (
          <>
            <Input
              label="Search for the winner camper"
              placeholder="Type a name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {searching && <p className="text-xs text-stone-400">Searching…</p>}
            <ul className="space-y-1 max-h-64 overflow-y-auto">
              {results.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    className="w-full text-left border border-stone-200 hover:border-forest-300 hover:bg-forest-50 rounded-lg px-3 py-2 transition-colors"
                    onClick={() => setWinner(c)}
                  >
                    <p className="text-sm font-medium text-stone-800">
                      {c.firstName} {c.lastName}
                    </p>
                    <p className="text-xs text-stone-500">
                      {c.dateOfBirth ?? "no DOB"}
                      {showSyncBadge && c.campMinderPersonId != null && " · CampMinder"}
                      {" · "}
                      {c.completionCount ?? 0} ACs
                    </p>
                  </button>
                </li>
              ))}
              {!searching && debouncedSearch && results.length === 0 && (
                <li className="text-xs text-stone-400">No matches.</li>
              )}
            </ul>
          </>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button
            variant="primary"
            disabled={!winner || previewLoading}
            loading={previewLoading}
            onClick={continueToReview}
          >
            Review merge
          </Button>
        </div>
      </div>
    );
  }

  // ─── Step: Review ────────────────────────────────────────────────────────
  if (step === "review" && preview && fields) {
    return (
      <ReviewStep
        preview={preview}
        fields={fields}
        setFields={setFields}
        completionPicks={completionPicks}
        setCompletionPicks={setCompletionPicks}
        enrollmentPicks={enrollmentPicks}
        setEnrollmentPicks={setEnrollmentPicks}
        include={include}
        setInclude={setInclude}
        // When the wizard was opened with a preselected winner there is no
        // step-1 to go back to; show the swap button instead.
        canGoBack={!initialWinner}
        onBack={() => setStep("pick-winner")}
        onSwap={swapWinnerAndLoser}
        swapping={previewLoading}
        onContinue={() => setStep("confirm")}
      />
    );
  }

  // ─── Step: Confirm ───────────────────────────────────────────────────────
  if (step === "confirm" && preview && fields && winner) {
    return (
      <ConfirmStep
        preview={preview}
        fields={fields}
        completionPicks={completionPicks}
        enrollmentPicks={enrollmentPicks}
        include={include}
        merging={merging}
        onBack={() => setStep("review")}
        onConfirm={submit}
        winner={winner}
        loser={loser}
      />
    );
  }

  return null;
}

// ───────────────────────────────────────────────────────────────────────────
// Review step

function FieldRow({
  label,
  field,
  preview,
  fields,
  setFields,
}: {
  label: string;
  field: keyof FieldChoices;
  preview: MergePreview;
  fields: FieldChoices;
  setFields: (f: FieldChoices) => void;
}) {
  const w = formatFieldValue(field, preview.winner);
  const l = formatFieldValue(field, preview.loser);
  const same = w === l;
  const choice = fields[field];
  const update = (v: FieldPick) => setFields({ ...fields, [field]: v });

  return (
    <div className="grid grid-cols-[140px_1fr_1fr] gap-3 items-start py-2 border-b border-stone-100 last:border-0">
      <div className="text-sm font-medium text-stone-700">{label}</div>
      <button
        type="button"
        disabled={same}
        onClick={() => update("winner")}
        className={`text-left rounded-lg border px-3 py-2 text-sm ${
          choice === "winner"
            ? "border-forest-500 bg-forest-50 text-forest-900"
            : "border-stone-200 hover:border-stone-300"
        } ${same ? "opacity-60 cursor-default" : "cursor-pointer"}`}
      >
        <span className="block text-[10px] uppercase tracking-wider text-stone-400 mb-0.5">
          Winner
        </span>
        <span className="break-words">{w}</span>
      </button>
      <button
        type="button"
        disabled={same}
        onClick={() => update("loser")}
        className={`text-left rounded-lg border px-3 py-2 text-sm ${
          choice === "loser"
            ? "border-forest-500 bg-forest-50 text-forest-900"
            : "border-stone-200 hover:border-stone-300"
        } ${same ? "opacity-60 cursor-default" : "cursor-pointer"}`}
      >
        <span className="block text-[10px] uppercase tracking-wider text-stone-400 mb-0.5">
          Loser
        </span>
        <span className="break-words">{l}</span>
      </button>
      {field === "notes" && !same && (preview.winner.notes || preview.loser.notes) && (
        <div className="col-span-3">
          <button
            type="button"
            onClick={() => update("concat")}
            className={`text-xs px-2 py-1 rounded ${
              choice === "concat"
                ? "bg-forest-600 text-white"
                : "bg-stone-100 hover:bg-stone-200 text-stone-700"
            }`}
          >
            Combine both notes
          </button>
        </div>
      )}
    </div>
  );
}

function CompletionRow({
  winnerC,
  loserC,
  pick,
  onPick,
}: {
  winnerC: PreviewCompletion | null;
  loserC: PreviewCompletion | null;
  pick: RowPick;
  onPick: (p: RowPick) => void;
}) {
  const conflict = !!winnerC && !!loserC;
  const credit = winnerC ?? loserC;
  if (!credit) return null;

  return (
    <div className="border border-stone-200 rounded-lg px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-stone-800">
            {credit.achievementCreditCode}{" "}
            <span className="font-normal text-stone-500">{credit.achievementCreditName}</span>
          </p>
          <p className="text-xs text-stone-500">
            {conflict ? (
              <>
                Winner: {winnerC.completedDate ?? "no date"} ({winnerC.status}) · Loser:{" "}
                {loserC.completedDate ?? "no date"} ({loserC.status})
              </>
            ) : winnerC ? (
              <>Winner only · {winnerC.completedDate ?? "no date"} ({winnerC.status})</>
            ) : loserC ? (
              <>Loser only · {loserC.completedDate ?? "no date"} ({loserC.status})</>
            ) : null}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {conflict ? (
            <>
              <PickButton selected={pick === "winner"} onClick={() => onPick("winner")}>
                Keep W
              </PickButton>
              <PickButton selected={pick === "loser"} onClick={() => onPick("loser")}>
                Keep L
              </PickButton>
              <PickButton selected={pick === "drop"} onClick={() => onPick("drop")}>
                Drop both
              </PickButton>
            </>
          ) : (
            <>
              <PickButton selected={pick !== "drop"} onClick={() => onPick("winner")}>
                Include
              </PickButton>
              <PickButton selected={pick === "drop"} onClick={() => onPick("drop")}>
                Drop
              </PickButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function EnrollmentRow({
  winnerE,
  loserE,
  pick,
  onPick,
}: {
  winnerE: PreviewEnrollment | null;
  loserE: PreviewEnrollment | null;
  pick: RowPick;
  onPick: (p: RowPick) => void;
}) {
  const conflict = !!winnerE && !!loserE;
  const enr = winnerE ?? loserE;
  if (!enr) return null;
  const dateRange = formatDateRange(enr.termStartDate, enr.termEndDate);
  const label = enr.termName ? `${enr.year} · ${enr.termName}` : `${enr.year} (year-only)`;

  return (
    <div className="border border-stone-200 rounded-lg px-3 py-2">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-stone-800">
            {label}
            {dateRange && (
              <span className="ml-2 text-xs font-normal text-stone-500">{dateRange}</span>
            )}
          </p>
          {conflict ? (
            <div className="text-xs text-stone-500 space-y-0.5 mt-0.5">
              <p>
                <span className="font-medium text-stone-600">Winner:</span>{" "}
                {describeEnrollment(winnerE)}
              </p>
              <p>
                <span className="font-medium text-stone-600">Loser:</span>{" "}
                {describeEnrollment(loserE)}
              </p>
            </div>
          ) : (
            <p className="text-xs text-stone-500 mt-0.5">
              {winnerE ? "Winner only · " : "Loser only · "}
              {describeEnrollment(enr)}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {conflict ? (
            <>
              <PickButton selected={pick === "winner"} onClick={() => onPick("winner")}>
                Keep W
              </PickButton>
              <PickButton selected={pick === "loser"} onClick={() => onPick("loser")}>
                Keep L
              </PickButton>
              <PickButton selected={pick === "drop"} onClick={() => onPick("drop")}>
                Drop both
              </PickButton>
            </>
          ) : (
            <>
              <PickButton selected={pick !== "drop"} onClick={() => onPick("winner")}>
                Include
              </PickButton>
              <PickButton selected={pick === "drop"} onClick={() => onPick("drop")}>
                Drop
              </PickButton>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function PickButton({
  selected,
  onClick,
  children,
}: {
  selected: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-2 py-1 rounded border ${
        selected
          ? "bg-forest-600 text-white border-forest-600"
          : "border-stone-200 hover:border-stone-300 text-stone-700"
      }`}
    >
      {children}
    </button>
  );
}

function ReviewStep({
  preview,
  fields,
  setFields,
  completionPicks,
  setCompletionPicks,
  enrollmentPicks,
  setEnrollmentPicks,
  include,
  setInclude,
  canGoBack,
  onBack,
  onSwap,
  swapping,
  onContinue,
}: {
  preview: MergePreview;
  fields: FieldChoices;
  setFields: (f: FieldChoices) => void;
  completionPicks: Record<number, RowPick>;
  setCompletionPicks: (m: Record<number, RowPick>) => void;
  enrollmentPicks: Record<string, RowPick>;
  setEnrollmentPicks: (m: Record<string, RowPick>) => void;
  include: IncludeChoices;
  setInclude: (i: IncludeChoices) => void;
  canGoBack: boolean;
  onBack: () => void;
  onSwap: () => void;
  swapping: boolean;
  onContinue: () => void;
}) {
  // Build unified completion list keyed by achievementCreditId
  const completionMap = new Map<
    number,
    { winnerC: PreviewCompletion | null; loserC: PreviewCompletion | null }
  >();
  for (const c of preview.winner.completions) {
    completionMap.set(c.achievementCreditId, { winnerC: c, loserC: null });
  }
  for (const c of preview.loser.completions) {
    const e = completionMap.get(c.achievementCreditId);
    if (e) e.loserC = c;
    else completionMap.set(c.achievementCreditId, { winnerC: null, loserC: c });
  }
  const completionRows = Array.from(completionMap.entries()).sort((a, b) => {
    const aName = (a[1].winnerC ?? a[1].loserC)!.achievementCreditCode;
    const bName = (b[1].winnerC ?? b[1].loserC)!.achievementCreditCode;
    return aName.localeCompare(bName);
  });

  const enrollmentMap = new Map<
    string,
    { winnerE: PreviewEnrollment | null; loserE: PreviewEnrollment | null }
  >();
  for (const e of preview.winner.enrollments) {
    enrollmentMap.set(e.key, { winnerE: e, loserE: null });
  }
  for (const e of preview.loser.enrollments) {
    const m = enrollmentMap.get(e.key);
    if (m) m.loserE = e;
    else enrollmentMap.set(e.key, { winnerE: null, loserE: e });
  }
  const enrollmentRows = Array.from(enrollmentMap.entries()).sort((a, b) => {
    const av = (a[1].winnerE ?? a[1].loserE)!.year;
    const bv = (b[1].winnerE ?? b[1].loserE)!.year;
    return bv - av;
  });

  return (
    <div className="space-y-5 max-h-[70vh] overflow-y-auto pr-1">
      <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
        <p className="font-semibold">Review what to keep</p>
        <p className="mt-1 text-amber-800">
          Pick which value wins for each camper field, and which ACs and enrollments to keep
          when both campers have one. Loser-only items are auto-included; toggle them to
          "Drop" to leave them out.
        </p>
      </div>

      <div className="rounded-lg border border-stone-200 px-3 py-2 flex items-center justify-between gap-3">
        <div className="text-sm text-stone-700">
          <span className="text-stone-500">Winner:</span>{" "}
          <strong>{preview.winner.firstName} {preview.winner.lastName}</strong>
          <span className="mx-2 text-stone-300">→</span>
          <span className="text-stone-500">Loser (will be deleted):</span>{" "}
          <strong>{preview.loser.firstName} {preview.loser.lastName}</strong>
        </div>
        <Button size="sm" variant="ghost" onClick={onSwap} disabled={swapping} loading={swapping}>
          Swap winner ⇄ loser
        </Button>
      </div>

      <section>
        <h3 className="text-sm font-semibold text-stone-800 mb-2">Camper fields</h3>
        <div className="border border-stone-200 rounded-lg px-3">
          {(Object.keys(FIELD_LABELS) as (keyof FieldChoices)[]).map((field) => (
            <FieldRow
              key={field}
              label={FIELD_LABELS[field]}
              field={field}
              preview={preview}
              fields={fields}
              setFields={setFields}
            />
          ))}
        </div>
        {preview.campMinderConflict && (
          <p className="text-xs text-amber-700 mt-1">
            ⚠ Both campers have different CampMinder IDs. Pick one above — the other will be
            cleared.
          </p>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-stone-800 mb-2">
          Achievement Credits ({completionRows.length} total
          {preview.completionConflicts.length > 0 &&
            `, ${preview.completionConflicts.length} conflict${
              preview.completionConflicts.length === 1 ? "" : "s"
            }`}
          )
        </h3>
        {completionRows.length === 0 ? (
          <p className="text-xs text-stone-500">Neither camper has any ACs.</p>
        ) : (
          <div className="space-y-1.5">
            {completionRows.map(([creditId, { winnerC, loserC }]) => (
              <CompletionRow
                key={creditId}
                winnerC={winnerC}
                loserC={loserC}
                pick={completionPicks[creditId] ?? "winner"}
                onPick={(p) =>
                  setCompletionPicks({ ...completionPicks, [creditId]: p })
                }
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-stone-800 mb-2">
          Enrollments ({enrollmentRows.length} total
          {preview.enrollmentConflicts.length > 0 &&
            `, ${preview.enrollmentConflicts.length} conflict${
              preview.enrollmentConflicts.length === 1 ? "" : "s"
            }`}
          )
        </h3>
        {enrollmentRows.length === 0 ? (
          <p className="text-xs text-stone-500">Neither camper has any enrollments.</p>
        ) : (
          <div className="space-y-1.5">
            {enrollmentRows.map(([key, { winnerE, loserE }]) => (
              <EnrollmentRow
                key={key}
                winnerE={winnerE}
                loserE={loserE}
                pick={enrollmentPicks[key] ?? "winner"}
                onPick={(p) => setEnrollmentPicks({ ...enrollmentPicks, [key]: p })}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="text-sm font-semibold text-stone-800 mb-2">Other records</h3>
        <div className="space-y-2 text-sm">
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={include.honors}
              onChange={(e) => setInclude({ ...include, honors: e.target.checked })}
            />
            Move {preview.loser.honorCount} honor{preview.loser.honorCount === 1 ? "" : "s"} from
            loser
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={include.assessments}
              onChange={(e) => setInclude({ ...include, assessments: e.target.checked })}
            />
            Move {preview.loser.assessmentCount} assessment
            {preview.loser.assessmentCount === 1 ? "" : "s"} from loser
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={include.linkedUsers}
              onChange={(e) => setInclude({ ...include, linkedUsers: e.target.checked })}
            />
            Re-point {preview.loser.linkedUserCount} linked user
            {preview.loser.linkedUserCount === 1 ? "" : "s"} to winner
          </label>
        </div>
      </section>

      <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-white pb-1">
        {canGoBack && (
          <Button variant="ghost" onClick={onBack}>Back</Button>
        )}
        <Button variant="primary" onClick={onContinue}>Continue</Button>
      </div>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Confirm step

function ConfirmStep({
  preview,
  fields,
  completionPicks,
  enrollmentPicks,
  include,
  merging,
  onBack,
  onConfirm,
  winner,
  loser,
}: {
  preview: MergePreview;
  fields: FieldChoices;
  completionPicks: Record<number, RowPick>;
  enrollmentPicks: Record<string, RowPick>;
  include: IncludeChoices;
  merging: boolean;
  onBack: () => void;
  onConfirm: () => void;
  winner: Camper;
  loser: Camper;
}) {
  const totalLoserCompletions = preview.loser.completions.length;
  const droppedCompletions = preview.loser.completions.filter(
    (c) => completionPicks[c.achievementCreditId] === "drop"
  ).length;
  const conflictsWonByLoser = preview.completionConflicts.filter(
    (id) => completionPicks[id] === "loser"
  ).length;

  const totalLoserEnrollments = preview.loser.enrollments.length;
  const droppedEnrollments = preview.loser.enrollments.filter(
    (e) => enrollmentPicks[e.key] === "drop"
  ).length;

  const fieldChanges = (Object.keys(fields) as (keyof FieldChoices)[])
    .filter((f) => fields[f] !== "winner")
    .map((f) => `${FIELD_LABELS[f]}: ${fields[f] === "concat" ? "combined" : "from loser"}`);

  return (
    <div className="space-y-4">
      <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-900">
        <p className="font-semibold">Confirm merge</p>
        <p className="mt-1 text-amber-800">
          <strong>{loser.firstName} {loser.lastName}</strong> will be permanently deleted after
          its records are moved into <strong>{winner.firstName} {winner.lastName}</strong>.
          This cannot be undone.
        </p>
      </div>

      <dl className="text-sm space-y-1.5">
        <div className="flex justify-between">
          <dt className="text-stone-500">From (loser, will be deleted)</dt>
          <dd className="font-medium">{loser.firstName} {loser.lastName}</dd>
        </div>
        <div className="flex justify-between">
          <dt className="text-stone-500">Into (winner)</dt>
          <dd className="font-medium">{winner.firstName} {winner.lastName}</dd>
        </div>
      </dl>

      <div className="border border-stone-200 rounded-lg p-3 text-sm space-y-1.5">
        <p className="font-medium text-stone-800">Summary</p>
        <ul className="list-disc list-inside text-stone-700 space-y-0.5 text-xs">
          <li>
            ACs: {totalLoserCompletions - droppedCompletions} of {totalLoserCompletions} loser
            ACs will move{conflictsWonByLoser > 0 && `, ${conflictsWonByLoser} replacing winner's`}.
            {droppedCompletions > 0 && ` ${droppedCompletions} will be dropped.`}
          </li>
          <li>
            Enrollments: {totalLoserEnrollments - droppedEnrollments} of{" "}
            {totalLoserEnrollments} loser enrollments will move.
            {droppedEnrollments > 0 && ` ${droppedEnrollments} will be dropped.`}
          </li>
          <li>
            Honors: {include.honors ? `${preview.loser.honorCount} moved` : "skipped (deleted)"}.
          </li>
          <li>
            Assessments:{" "}
            {include.assessments
              ? `${preview.loser.assessmentCount} moved`
              : "skipped (deleted)"}.
          </li>
          <li>
            Linked users:{" "}
            {include.linkedUsers
              ? `${preview.loser.linkedUserCount} re-pointed`
              : `${preview.loser.linkedUserCount} unlinked`}.
          </li>
          {fieldChanges.length > 0 && (
            <li>Field changes on winner: {fieldChanges.join("; ")}.</li>
          )}
        </ul>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onBack} disabled={merging}>Back</Button>
        <Button variant="danger" loading={merging} onClick={onConfirm}>
          Confirm merge
        </Button>
      </div>
    </div>
  );
}
